/**
 * serviceOrderHeader — os campos de CABEÇALHO da OS que não são dinheiro, item,
 * diagnóstico nem equipamento: contato, prazo prometido, responsáveis,
 * pagamento, garantia e condições.
 *
 * ─── POR QUE UM BUILDER SÓ PARA NOVE COLUNAS ────────────────────────────────
 *
 * `service_orders` é gravada por LISTA EXPLÍCITA de colunas em SEIS lugares (o
 * hook online, os dois executores offline, o formulário — criação e edição — e
 * a duplicação). Nove colunas × seis payloads = 54 linhas para alguém esquecer
 * uma. E o modo de falha é sempre o mesmo e sempre mudo: o save dá certo, o
 * toast é de sucesso, e o campo some (CLAUDE.md #33/#62).
 *
 * Por isso: `...buildServiceOrderHeaderColumns(...)` em cada payload, e
 * {@link SERVICE_ORDER_HEADER_COLUMNS} para o executor de UPDATE, que precisa
 * copiar campo a campo (ele só pode escrever o que veio na fila — ver #89).
 *
 * ─── O QUE NÃO ESTÁ AQUI, E POR QUÊ ─────────────────────────────────────────
 *
 * **Dinheiro** (`discount`/`discount_type`/`shipping`/`total`) passa por
 * `buildQuoteTotalsColumns` — a fonte única da ordem de aplicação dos descontos
 * e do arredondamento (#69). A OS usa as MESMAS colunas e a MESMA conta do
 * orçamento; qualquer segunda implementação faria a tela, o PDF e o banco
 * discordarem do total.
 *
 * **A assinatura do cliente** tem builder PRÓPRIO ({@link
 * buildServiceOrderSignatureColumns}) e isso é deliberado: ela é capturada na
 * tela de detalhe, na entrega, e o formulário de edição não a conhece. Se ela
 * estivesse neste builder, salvar uma edição qualquer da OS gravaria `null` por
 * cima da assinatura já coletada — o #70 ao contrário, apagando uma prova.
 */
import { normalizePaymentMethods } from '@/lib/quotePaymentInfo';
import { normalizeWarranty, type WarrantyUnit } from '@/lib/quoteWarranty';
import type { PaymentMethod } from '@/types/database';

/** Os campos, em camelCase, como o formulário os carrega. */
export interface ServiceOrderHeaderInfo {
  clientEmail?: string | null;
  /** ISO do prazo PROMETIDO ao cliente (com hora). Ver a nota abaixo. */
  expectedDate?: string | null;
  attendantName?: string | null;
  technicianName?: string | null;
  /** `null` = não informado (legado). `[]` = escolha explícita de nenhuma (#73). */
  paymentMethods?: readonly string[] | null;
  paymentTerms?: string | null;
  warrantyAmount?: number | string | null;
  warrantyUnit?: unknown;
  termsConditions?: string | null;
}

/**
 * As colunas escritas por {@link buildServiceOrderHeaderColumns}, na ordem do
 * builder. Existe para o executor offline de `update_service_order` iterar em
 * vez de repetir nove `assign(...)` — que é exatamente onde o diagnóstico já
 * ficou para trás uma vez (#89).
 */
export const SERVICE_ORDER_HEADER_COLUMNS = [
  'client_email',
  'expected_date',
  'attendant_name',
  'technician_name',
  'payment_methods',
  'payment_terms',
  'warranty_amount',
  'warranty_unit',
  'terms_conditions',
] as const;

const text = (raw: unknown): string | null => {
  if (raw === null || raw === undefined) return null;
  const value = String(raw).trim();
  return value.length > 0 ? value : null;
};

/**
 * `expected_date` é um instante de verdade (com hora), diferente de
 * `scheduled_date`, que é date-only guardado à meia-noite UTC e lido assim pela
 * Agenda (`localDateFromIsoDatePart`). Não confunda os dois: `scheduled_date`
 * é QUANDO O SERVIÇO ACONTECE (vai para o calendário), `expected_date` é a
 * PREVISÃO DE ENTREGA prometida ao cliente.
 *
 * Data ilegível vira `null` em vez de `Invalid Date`: um carimbo quebrado no
 * banco reapareceria como "NaN" no PDF que o cliente recebe.
 */
export function normalizeExpectedDate(raw: unknown): string | null {
  const value = text(raw);
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

/**
 * As nove colunas prontas para um payload de `service_orders`.
 *
 * Espalhe (`...buildServiceOrderHeaderColumns(data)`) em vez de escrever as
 * chaves à mão — extrair para uma variável e espalhar a variável DESLIGA o
 * guard de varredura (#75).
 *
 * Aceita camelCase (formulário) e snake_case (fila offline, cache, linha do
 * banco) na mesma chamada, porque os executores recebem a linha já em nome de
 * coluna e não teriam como reconvertê-la sem repetir esta lista.
 */
export function buildServiceOrderHeaderColumns(
  // `unknown` de propósito — ver a nota do parágrafo acima: os chamadores usam
  // camelCase (formulário) e snake_case (fila offline, cache, linha do banco).
  info: unknown,
): {
  client_email: string | null;
  expected_date: string | null;
  attendant_name: string | null;
  technician_name: string | null;
  payment_methods: PaymentMethod[] | null;
  payment_terms: string | null;
  warranty_amount: number | null;
  warranty_unit: WarrantyUnit | null;
  terms_conditions: string | null;
} {
  const src = (info ?? {}) as Record<string, unknown>;
  const { warrantyAmount, warrantyUnit } = normalizeWarranty(
    (src.warrantyAmount ?? src.warranty_amount) as number | string | null | undefined,
    src.warrantyUnit ?? src.warranty_unit,
  );

  return {
    client_email: text(src.clientEmail ?? src.client_email),
    expected_date: normalizeExpectedDate(src.expectedDate ?? src.expected_date),
    attendant_name: text(src.attendantName ?? src.attendant_name),
    technician_name: text(src.technicianName ?? src.technician_name),
    // `null` × `[]` é preservado de propósito: no orçamento é essa distinção
    // que decide o QR de PIX (#73), e a OS grava a mesma coluna.
    payment_methods: normalizePaymentMethods(src.paymentMethods ?? src.payment_methods),
    payment_terms: text(src.paymentTerms ?? src.payment_terms),
    warranty_amount: warrantyAmount,
    warranty_unit: warrantyUnit,
    terms_conditions: text(src.termsConditions ?? src.terms_conditions),
  };
}

/**
 * O leitor — a metade que o guard de escrita não cobre (#70). Perder um campo
 * aqui faz a tela reabrir sem ele e o PRÓXIMO save gravar `null`: o dado
 * desaparece sozinho, sem ninguém ter apagado nada.
 */
export function serviceOrderHeaderFromRow(
  row: unknown,
): Required<Omit<ServiceOrderHeaderInfo, 'paymentMethods' | 'warrantyAmount' | 'warrantyUnit'>> & {
  paymentMethods: PaymentMethod[] | null;
  warrantyAmount: number | null;
  warrantyUnit: WarrantyUnit | null;
} {
  const columns = buildServiceOrderHeaderColumns(row);
  return {
    clientEmail: columns.client_email,
    expectedDate: columns.expected_date,
    attendantName: columns.attendant_name,
    technicianName: columns.technician_name,
    paymentMethods: columns.payment_methods,
    paymentTerms: columns.payment_terms,
    warrantyAmount: columns.warranty_amount,
    warrantyUnit: columns.warranty_unit,
    termsConditions: columns.terms_conditions,
  };
}

// ============================================================================
// ASSINATURA DO CLIENTE (builder à parte — ver a nota no topo do arquivo)
// ============================================================================

export interface ServiceOrderClientSignature {
  /** Data URL PNG do traço, como `receipts.issuer_signature`. */
  signature: string | null;
  signerName: string | null;
  /** ISO do instante em que assinou. */
  signedAt: string | null;
}

/**
 * As três colunas da assinatura. Só a tela de detalhe usa este builder, e ele
 * é ALL-OR-NOTHING: sem traço, nome e carimbo caem para `null` juntos (é o que
 * o CHECK do banco exige, e um carimbo sem desenho afirmaria uma assinatura que
 * não existe).
 */
export function buildServiceOrderSignatureColumns(
  input: { signature?: string | null; signerName?: string | null; signedAt?: string | null } | null | undefined,
): { client_signature: string | null; client_signer_name: string | null; client_signed_at: string | null } {
  const signature = text(input?.signature);
  if (!signature) {
    return { client_signature: null, client_signer_name: null, client_signed_at: null };
  }
  return {
    client_signature: signature,
    client_signer_name: text(input?.signerName),
    client_signed_at: normalizeExpectedDate(input?.signedAt) ?? new Date().toISOString(),
  };
}

/**
 * O leitor da assinatura, para a tela e para o payload do PDF.
 *
 * ⚠️ NÃO reaproveita o builder de propósito: o builder CARIMBA a hora quando ela
 * falta (é o instante da coleta), e fazer isso na leitura inventaria uma data de
 * assinatura a cada render — um documento com valor probatório afirmando um
 * instante que ninguém registrou. Na leitura, ausente é ausente.
 */
export function serviceOrderSignatureFromRow(row: unknown): ServiceOrderClientSignature {
  const src = (row ?? {}) as Record<string, unknown>;
  const signature = text(src.client_signature);
  if (!signature) return { signature: null, signerName: null, signedAt: null };
  return {
    signature,
    signerName: text(src.client_signer_name),
    signedAt: normalizeExpectedDate(src.client_signed_at),
  };
}
